//#region Util Imports

import * as _ from "underscore"

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'

//#endregion

import { Api, LocalDictionaryFactory } from './../api/api.module'

import { ColumnModel } from '../api/api.models'
import { OpTariff } from '../../models/core/op-tariff'
import { Activity, Enums } from '../../models/common'

import { GridService, GridContextMenuService } from '../../services/grid'
import { UserSessionService } from '../../services/user-session'
import { CommonHelper, ConfigHelper, EventsNames, LocalizationHelper, TemplateHelper } from '../../helpers'
import { TariffService } from '../../services/tariff/tariff.service'
import { SystemService } from '../../services/system/system.service'

import { BaseGridController, ScopeBaseGrid } from '../common/common.base.grid.controller'
import { ButtonsOptions } from '../common/grid/grid.controller'
import { CreateEditInitParams, BulkEditInitParams } from '../common/create-edit-popup/create-edit-popup.controller'
import { GridTypes, GridController } from '../common/grid/grid.controller'
import { LoggerService } from './../common/common.module'
import { GridCustomButtonsOptions, GridCustomButtonLocation } from '../common/grid'

export interface TariffScope extends ScopeBaseGrid {
    deleteConfirmationOptions: {
        title: string
        onConfirmed: () => void
    }
}

export class TariffController extends BaseGridController {

    // #region Consts

    private get GRID_NAME() { return 'TariffsGrid' }

    // #endregion


    // #region Members

    private grid: GridController<OpTariff>
    private copyTariffGridButton: DevExpress.ui.dxButton

    // #endregion


    // #region Ctor

    constructor(
        protected $scope: TariffScope,
        $state: StateService,
        $timeout: ng.ITimeoutService,
        api: Api,
        private logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        protected userSessionService: UserSessionService,
        private gridService: GridService,
        private gridContextMenuService: GridContextMenuService,
        private tariffService: TariffService,
        protected systemService: SystemService,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $state, userSessionService, systemService, api, localDictionary, null, referenceType)

        this.setConfirmationsOptions()
        this.setEvents()
        this.setMainGridOptions()
    }

    // #endregion


    // #region Eventsopen_cerate

    private onInitialized(e: any, grid: GridController<OpTariff>): void {
        this.grid = grid
        this.dataGrid = e.component

        this.setGridId(e.tableId)
    }

    private onAdd(idTariffToCopy: number = 0): void {
        const createEditInitParams: CreateEditInitParams = this.tariffService.getCreateEditOptions(idTariffToCopy || 0, undefined, !!idTariffToCopy)
        this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
    }

    private onEdit(): void {
        if (this.dataGrid.getSelectedRowKeys().length === 1) {
            const createEditInitParams: CreateEditInitParams = this.tariffService.getCreateEditOptions(this.grid.selectedItemId)
            this.$scope.$broadcast(EventsNames.INITIALIZE_CREATE_EDIT_POPUP, createEditInitParams)
        } else {
            const columnCount: number = this.dataGrid.columnCount()
            const columns: any[] = []

            for (let i = 0; i < columnCount; i++) {
                columns.push(this.dataGrid.columnOption(i));
            }

            const bulkEditInitParams: BulkEditInitParams = {
                gridColumns: columns,
                groupReferenceType: null,
                groupReferenceValue: null,
                idReferenceType: this.referenceType,
                moduleName: this.moduleName,
                section: null,
                selectedGridRows: angular.copy(this.grid.selectedGridItems),
                useBusinessObjects: true,
            }

            this.$scope.$root.$broadcast(EventsNames.INITIALIZE_BULK_EDIT_POPUP, bulkEditInitParams)
        }
    }

    private onShowDetails(e: any): void {
        this.systemService.openDetails({
            moduleName: this.moduleName,
            pageItemsIds: this.grid.pageItemsIds,
            referenceObjectId: this.grid.selectedItemId
        })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.popupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onDeleted(): void {
        this.grid.dxGrid.refresh()

    }

    private onConfirmDelete(): void {
        this.tariffService
            .deleteTariff(this.grid.selectedItemId, this.moduleName)
            .then(() => {
                this.onDeleted()
            })
            .fail((error: any) => {
                this.logger.log(error)
            })
    }

    private onGridSelectionChanged(e: any): void {
        this.toggleCopyTariffButton()
    }

    private onCopy(): void {
        this.onAdd(this.grid.selectedItemId)
    }

    private toggleCopyTariffButton(): void {
        if (this.copyTariffGridButton != null) {
            this.copyTariffGridButton.option('disabled', !this.areCopyTariffCompletable())
        }
    }

    private areCopyTariffCompletable(): boolean {
        return _.size(this.grid.selectedItems) > 0
    }

    // #endregion

    private getGridButtonsOptions(): ButtonsOptions {
        return {
            enabled: true,
            addPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_ADD) ? "['" + Activity[Activity.TARIFF_ADD] + "']" : null,
            deletePermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_DEL) ? "['" + Activity[Activity.TARIFF_DEL] + "']" : null,
            editPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_EDIT) ? "['" + Activity[Activity.TARIFF_EDIT] + "']" : null,
            importPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_IMPORT_OP) ? "['" + Activity[Activity.TARIFF_IMPORT_OP] + "']" : null,
            exportPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_EXPORT_OP) ? "['" + Activity[Activity.TARIFF_EXPORT_OP] + "']" : null,
            saveColumnSettingsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP) ? "['" + Activity[Activity.WEBSIMAX_MODULE_VIEW_COLUMN_DEFINITION_OP] + "']" : null,
            showDetailsPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_LIST) ? "['" + Activity[Activity.TARIFF_LIST] + "']" : null,

            onAddClick: () => this.onAdd(),
            onDeleteClick: () => this.onDelete(),
            onEditClick: () => this.onEdit(),
            onShowClick: (e: any) => this.onShowDetails(e),
        }
    }

    private getContextMenuItems(isTouchScreen: boolean = false): any[] {
        const items = this.gridContextMenuService.getContextMenuItems(Enums.SimaxModuleName.Tariff, {
            idReferenceTypes: this.grid.selectedItemsIds,

            onEdit: () => {
                this.onEdit()
            },

            onCopy: () => {
                this.onCopy()
            },

        })

        return this.mergeWithDefaultItems(isTouchScreen, items);
    }

    private getGridCustomButtonOptions(): GridCustomButtonsOptions {
        const buttons: GridCustomButtonsOptions = { buttons: [] }

        if (this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_COPY)) {
            buttons.buttons.push({
                location: GridCustomButtonLocation.Dropdown,
                options: {
                    template: TemplateHelper.getIcon(ConfigHelper.Icon.CONTENT_COPY, this.userSession),
                    hint: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CopyTariff),

                    onClick: (e: any) => {
                        this.onCopy()
                    },

                    onInitialized: (e: any) => {
                        this.copyTariffGridButton = e.component
                        this.toggleCopyTariffButton()
                    }
                }
            })
        }
        return buttons
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onDeleted()
        )

        this.$scope.showDetails = (e: any) => this.onShowDetails(e)
    }

    private setMainGridOptions(): void {
        this.gridService
            .getColumnModelByCategory([
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
                Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
            ], this.moduleName)
            .then((columns: ColumnModel[], defaultColumns: ColumnModel[]) => {
                this.$scope.mainGridOptions = {
                    referenceId: this.referenceType,
                    idReferenceType: this.referenceType,
                    type: GridTypes.MAIN,
                    name: this.GRID_NAME,
                    addCustomFilters: true,
                    buttonsOptions: this.getGridButtonsOptions(),
                    customButtonOptions: this.getGridCustomButtonOptions(),
                    columnsDictionary: columns,
                    dataSourceAdditionalFields: null,
                    defaultColumnsDictionary: defaultColumns,
                    localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.tariffSaved,
                    preExecuteClearFilter: this.preExecuteGridClearFilter,
                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.TariffModuleMainGrid),
                    getDataSourceMethod: this.userSession
                        .getDataProvider().Tariff.getTariffFilter,
                    onInitialized: (e: any, grid: GridController<OpTariff>) => this.onInitialized(e, grid),
                    onDataReady: (data: any) => this.onDataReady(data),
                    onRowDoubleClick: (e: any) => this.onShowDetails(e),
                    contextMenuItems: () => this.getContextMenuItems(),
                    onSelectionChanged: (e: any) => this.onGridSelectionChanged(e)
                }
            })
    }
}
